import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Calendar,
  Copy,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileText,
  Mail,
  MapPin,
  Phone,
  Shield,
  User,
  RefreshCw,
  X,
} from "lucide-react";
import "../styles/admin-base.css";
import "../styles/registration-review.css";
import client from "../../src/api/client";
import { getMembershipFormPreviewUrl } from "../utils/exportMembershipPdf";
import { API_BASE_URL } from "../../src/config";
import { resolveBackendAssetUrl } from "../../src/utils/assetUrl";

const DEFAULT_ADMIN_ID = (() => {
  const envValue = process.env.REACT_APP_DEFAULT_ADMIN_ID;
  const parsed = envValue ? Number.parseInt(envValue, 10) : Number.NaN;
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return 1;
})();

const DEFAULT_ADMIN_NAME = (() => {
  const envValue = process.env.REACT_APP_DEFAULT_ADMIN_NAME;
  if (typeof envValue === "string" && envValue.trim().length) {
    return envValue.trim();
  }
  return "Admin Portal";
})();

const readStoredAdminActor = () => {
  if (typeof window === "undefined" || !window?.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem("adminUser");
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const candidateIds = [parsed.id, parsed.adminId, parsed?.admin?.id]
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const resolvedId = candidateIds[0] ?? null;

    if (!resolvedId) {
      return null;
    }

    const firstName = parsed.firstName ?? parsed?.admin?.firstName ?? "";
    const lastName = parsed.lastName ?? parsed?.admin?.lastName ?? "";
    const fallbackName = parsed.email ?? DEFAULT_ADMIN_NAME;
    const resolvedName = [firstName, lastName].map((part) => String(part || "").trim()).filter(Boolean).join(" ") || fallbackName;

    return {
      id: resolvedId,
      name: resolvedName,
    };
  } catch (error) {
    console.warn("Unable to read admin actor info", error);
    return null;
  }
};

const resolveActorContext = () => {
  const stored = readStoredAdminActor();
  if (stored) {
    return stored;
  }

  return {
    id: DEFAULT_ADMIN_ID,
    name: DEFAULT_ADMIN_NAME,
  };
};

const rejectionReasons = [
  "Incomplete documentation",
  "Invalid company information",
  "Duplicate registration detected",
  "Invalid contact information",
  "Photo quality issues",
  "Employment verification failed",
  "Other (specify below)",
];

const defaultSummary = {
  totalPending: 0,
  highPriority: 0,
  duplicates: 0,
  docIssues: 0,
};

const queueFilterOptions = [
  { value: "all-pending", label: "All Pending" },
  { value: "high-risk", label: "High Risk" },
  { value: "normal", label: "Normal" },
  { value: "document-issue", label: "Document Issue" },
];

export default function RegistrationReview() {
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [selectedId, setSelectedId] = useState(null);
  const [queueFilter, setQueueFilter] = useState("all-pending");
  const [activeTab, setActiveTab] = useState("details");
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(rejectionReasons[0]);
  const [customReason, setCustomReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [actionErrors, setActionErrors] = useState({ approve: "", reject: "" });
  const [currentAction, setCurrentAction] = useState(null);
  const [formPreviewing, setFormPreviewing] = useState(false);
  const [formPreviewError, setFormPreviewError] = useState("");
  const [showFormPreviewModal, setShowFormPreviewModal] = useState(false);
  const [formPreviewUrl, setFormPreviewUrl] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(0);
  const [historyColumnWidths, setHistoryColumnWidths] = useState([]);
  const historyResizeRef = useRef(null);
  const historyTableRef = useRef(null);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState("");
  const [similarComparison, setSimilarComparison] = useState({ source: null, matches: [] });
  const [refreshTick, setRefreshTick] = useState(0);

  const isApproveProcessing = currentAction === "approve";
  const isRejectProcessing = currentAction === "reject";

  const filteredQueue = useMemo(() => {
    if (queueFilter === "high-risk") {
      return queue.filter((entry) => entry.priority === "High");
    }
    if (queueFilter === "normal") {
      return queue.filter((entry) => entry.priority === "Normal");
    }
    if (queueFilter === "document-issue") {
      return queue.filter((entry) => entry.validationChecks?.documentsComplete === false);
    }
    return queue;
  }, [queue, queueFilter]);

  const selectedRegistration = useMemo(
    () => filteredQueue.find((entry) => entry.id === selectedId) ?? null,
    [filteredQueue, selectedId],
  );

  const applyQueuePayload = useCallback((payload) => {
    const nextQueue = payload?.queue ?? [];
    setQueue(nextQueue);
    setSummary(payload?.summary ?? defaultSummary);
  }, []);

  const buildActionErrorMessage = useCallback((err, fallback) => {
    if (!err) return fallback;
    const payload = err?.response?.data ?? {};
    const candidates = [payload.message, payload.error, err.message, fallback];
    const resolved = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length);
    return resolved ?? fallback;
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError("");
      const response = await client.get("/api/admin/registrations/history");
      setHistory(response.data?.history ?? []);
    } catch (err) {
      setHistoryError(err?.response?.data?.message ?? "Unable to load registration history.");
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    let subscribed = true;

    const fetchQueue = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await client.get("/api/admin/registrations/review");
        if (!subscribed) return;
        applyQueuePayload(response.data ?? {});
        loadHistory();
      } catch (err) {
        if (!subscribed) return;
        setError(err?.response?.data?.message ?? "Unable to load registration queue.");
        setQueue([]);
        setSummary(defaultSummary);
        setSelectedId(null);
      } finally {
        if (subscribed) {
          setLoading(false);
        }
      }
    };

    fetchQueue();
    return () => {
      subscribed = false;
    };
  }, [applyQueuePayload, loadHistory, refreshTick]);

  useEffect(() => {
    if (!filteredQueue.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !filteredQueue.some((entry) => entry.id === selectedId)) {
      setSelectedId(filteredQueue[0].id);
      setActiveTab("details");
    }
  }, [filteredQueue, selectedId]);

  useEffect(() => {
    setPreviewDocument(null);
    setCopyStatus("");
  }, [selectedRegistration]);

  const documentFiles = (selectedRegistration?.documentFiles ?? []).filter((doc) => {
    const label = String(doc?.label ?? "").toLowerCase();
    const key = String(doc?.key ?? "").toLowerCase();
    const isIdPhoto = (label.includes("id") && label.includes("photo")) || (key.includes("id") && key.includes("photo"));
    const isSignature = label.includes("signature") || key.includes("signature");
    return isIdPhoto || isSignature;
  });
  const hasPhotoId = documentFiles.some((doc) => {
    const label = String(doc?.label ?? "").toLowerCase();
    const key = String(doc?.key ?? "").toLowerCase();
    const isIdPhoto = (label.includes("id") && label.includes("photo")) || (key.includes("id") && key.includes("photo"));
    return isIdPhoto && Boolean(doc?.filePath);
  });
  const idPhotoEntry = documentFiles.find((doc) => doc?.key === "id_photo") ?? null;
  const signatureEntry = documentFiles.find((doc) => doc?.key === "signature") ?? null;

  const overview = useMemo(() => {
    const computed = {
      totalPending: queue.length,
      highPriority: queue.filter((entry) => entry.priority === "High").length,
      duplicates: queue.filter((entry) => entry.duplicateFlag).length,
      docIssues: queue.filter((entry) => !(entry.validationChecks?.documentsComplete)).length,
    };
    return {
      totalPending: summary?.totalPending ?? computed.totalPending,
      highPriority: summary?.highPriority ?? computed.highPriority,
      duplicates: summary?.duplicates ?? computed.duplicates,
      docIssues: summary?.docIssues ?? computed.docIssues,
    };
  }, [queue, summary]);

  const historyPageSize = 8;
  const historyTotalPages = Math.max(1, Math.ceil(history.length / historyPageSize));
  const historyPageClamped = Math.min(historyPage, historyTotalPages - 1);
  const historyPageStart = historyPageClamped * historyPageSize;
  const pagedHistory = history.slice(historyPageStart, historyPageStart + historyPageSize);

  const guidedActions = useMemo(() => {
    const fastTrackCount = queue.filter((entry) => {
      const checks = entry.validationChecks ?? {};
      return Boolean(
        checks.emailValid
          && checks.phoneValid
          && checks.companyVerified
          && checks.noDuplicates
          && checks.documentsComplete,
      );
    }).length;

    const manualReviewCount = queue.filter((entry) => {
      if (entry.priority === "High") return true;
      const checks = entry.validationChecks ?? {};
      return Boolean(entry.duplicateFlag || !checks.documentsComplete || !checks.phoneValid);
    }).length;

    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const approvalsWeek = history.filter(
      (entry) => entry.action === "Approved" && new Date(entry.createdAt).getTime() >= weekAgo,
    ).length;
    const rejectionsWeek = history.filter(
      (entry) => entry.action === "Rejected" && new Date(entry.createdAt).getTime() >= weekAgo,
    ).length;
    const awaitingDocs = overview.docIssues;

    const pluralize = (value, word) => `${value} ${word}${value === 1 ? "" : "s"}`;

    return {
      fastTrackCount,
      manualReviewCount,
      approvalsWeek,
      rejectionsWeek,
      awaitingDocs,
      pluralize,
    };
  }, [history, overview.docIssues, queue]);

  const formatDate = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTime = (value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    setHistoryPage(0);
  }, [history.length]);

  useEffect(() => {
    if (!historyTableRef.current) return;
    if (historyColumnWidths.length) return;
    const headers = historyTableRef.current.querySelectorAll("thead th");
    if (!headers.length) return;
    const tableWidth = historyTableRef.current.parentElement?.clientWidth ?? 0;
    const fixedWidths = [240, 110, 260, 0, 140];
    const fixedTotal = fixedWidths.reduce((sum, width) => sum + (width || 0), 0);
    const reasonMin = 240;
    const reasonWidth = tableWidth
      ? Math.max(reasonMin, tableWidth - fixedTotal)
      : 320;
    const resolved = [...fixedWidths];
    resolved[3] = reasonWidth;
    setHistoryColumnWidths(resolved);
  }, [history.length, historyColumnWidths.length]);

  const startHistoryResize = useCallback((index, event) => {
    if (!historyTableRef.current) return;
    event.preventDefault();
    const headers = historyTableRef.current.querySelectorAll("thead th");
    const currentWidth = headers[index]?.offsetWidth ?? 160;
    historyResizeRef.current = {
      index,
      startX: event.clientX,
      startWidth: currentWidth,
      headers,
    };

    const handleMouseMove = (moveEvent) => {
      if (!historyResizeRef.current) return;
      const { startX, startWidth, index: columnIndex, headers: headerCells } = historyResizeRef.current;
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(40, startWidth + delta);
      setHistoryColumnWidths((prev) => {
        const base = prev.length
          ? [...prev]
          : Array.from(headerCells).map((cell) => cell.offsetWidth);
        const tableWidth = historyTableRef.current?.parentElement?.clientWidth ?? 0;
        const otherWidths = base.reduce(
          (sum, width, idx) => (idx === columnIndex ? sum : sum + width),
          0,
        );
        const available = Math.max(0, tableWidth - otherWidths);
        const minWidth = 40;
        const clampedWidth = available < minWidth
          ? available
          : Math.min(Math.max(minWidth, nextWidth), available);
        base[columnIndex] = clampedWidth;
        return base;
      });
    };

    const handleMouseUp = () => {
      historyResizeRef.current = null;
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, []);

  const getInitials = (value) => {
    if (!value) return "?";
    return (
      value
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?"
    );
  };

  const resolveRegistrationAvatar = (registration) => {
    if (!registration || typeof registration !== "object") {
      return null;
    }

    const idPhotoFromDocuments = Array.isArray(registration.documentFiles)
      ? registration.documentFiles.find((doc) => doc?.key === "id_photo")?.filePath ?? null
      : null;

    const avatarCandidate = [
      registration.profilePicture,
      registration.profilePhoto,
      registration.photo,
      registration.avatar,
      registration.photoUrl,
      registration.photoURL,
      registration.avatarUrl,
      registration.profileImage,
      registration.idPhotoPath,
      registration.idDocument,
      idPhotoFromDocuments,
      registration.user?.profilePicture,
      registration.user?.profilePhoto,
      registration.user?.photo,
      registration.user?.avatar,
      registration.user?.photoUrl,
      registration.user?.photoURL,
      registration.user?.avatarUrl,
      registration.user?.profileImage,
    ].find((value) => typeof value === "string" && value.trim().length);

    return resolveBackendAssetUrl(avatarCandidate ?? null, { apiBaseUrl: API_BASE_URL });
  };

  const renderRegistrationAvatar = (registration, sizeClassName) => {
    const avatarSrc = resolveRegistrationAvatar(registration);
    const initials = getInitials(registration?.fullName);
    const displayName = String(registration?.fullName ?? "Member").trim() || "Member";

    return (
      <div className={`admin-avatar ${sizeClassName} ${avatarSrc ? "has-image" : ""}`.trim()}>
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={`${displayName} profile`}
            className="rr-avatar__image"
            loading="lazy"
          />
        ) : (
          initials
        )}
      </div>
    );
  };

  const resetRejectionForm = () => {
    setRejectionReason(rejectionReasons[0]);
    setCustomReason("");
  };

  const closeApproveDialog = () => {
    setActionErrors((prev) => ({ ...prev, approve: "" }));
    setShowApproveDialog(false);
  };

  const closeRejectDialog = () => {
    setActionErrors((prev) => ({ ...prev, reject: "" }));
    resetRejectionForm();
    setShowRejectDialog(false);
  };

  const closeSimilarModal = () => {
    setShowSimilarModal(false);
  };

  const openSimilarModal = async () => {
    if (!selectedRegistration?.userId) return;
    setShowSimilarModal(true);
    setSimilarLoading(true);
    setSimilarError("");
    setSimilarComparison({ source: null, matches: [] });

    try {
      const response = await client.get(
        `/api/admin/registrations/${encodeURIComponent(selectedRegistration.userId)}/similar-members`,
      );
      const payload = response?.data ?? {};
      setSimilarComparison({
        source: payload?.source ?? null,
        matches: Array.isArray(payload?.matches) ? payload.matches : [],
      });
    } catch (err) {
      setSimilarError(err?.response?.data?.message ?? "Unable to load similar members.");
      setSimilarComparison({ source: null, matches: [] });
    } finally {
      setSimilarLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRegistration?.userId) return;
    setActionErrors((prev) => ({ ...prev, approve: "" }));
    setCurrentAction("approve");
    try {
      const { id: actorAdminId, name: actorName } = resolveActorContext();
      const response = await client.post(`/api/admin/registrations/${selectedRegistration.userId}/approve`, {
        actorAdminId,
        actorName,
      });
      applyQueuePayload(response.data ?? {});
      loadHistory();
      closeApproveDialog();
    } catch (err) {
      setActionErrors((prev) => ({
        ...prev,
        approve: buildActionErrorMessage(err, "Unable to approve registration. Please try again."),
      }));
    } finally {
      setCurrentAction(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration?.userId) return;
    const remarks = customReason.trim();
    const finalReason = rejectionReason === "Other (specify below)"
      ? remarks
      : remarks
        ? `${rejectionReason} - ${remarks}`
        : rejectionReason;

    if (!finalReason) {
      setActionErrors((prev) => ({ ...prev, reject: "Please provide a rejection reason." }));
      return;
    }

    setActionErrors((prev) => ({ ...prev, reject: "" }));
    setCurrentAction("reject");
    try {
      const { id: actorAdminId, name: actorName } = resolveActorContext();
      const response = await client.post(`/api/admin/registrations/${selectedRegistration.userId}/reject`, {
        reason: finalReason,
        category: rejectionReason,
        actorAdminId,
        actorName,
      });
      applyQueuePayload(response.data ?? {});
      loadHistory();
      closeRejectDialog();
    } catch (err) {
      setActionErrors((prev) => ({
        ...prev,
        reject: buildActionErrorMessage(err, "Unable to reject registration. Please try again."),
      }));
    } finally {
      setCurrentAction(null);
    }
  };

  const buildPreviewMember = () => {
    if (!selectedRegistration) return null;
    const address = selectedRegistration.address
      ?? [
        selectedRegistration.addressLine1,
        selectedRegistration.addressLine2,
        selectedRegistration.city,
        selectedRegistration.province,
        selectedRegistration.postalCode,
      ].filter(Boolean).join(", ");

    return {
      fullName: selectedRegistration.fullName,
      email: selectedRegistration.email,
      phone: selectedRegistration.phone,
      mobile: selectedRegistration.phone,
      address,
      placeOfBirth: selectedRegistration.placeOfBirth,
      dateOfBirth: selectedRegistration.dateOfBirth,
      gender: selectedRegistration.gender,
      genderOther: selectedRegistration.genderOther,
      maritalStatus: selectedRegistration.maritalStatus,
      numberOfChildren: selectedRegistration.numberOfChildren,
      religion: selectedRegistration.religion,
      education: selectedRegistration.education,
      company: selectedRegistration.company,
      position: selectedRegistration.position,
      department: selectedRegistration.department,
      yearsEmployed: selectedRegistration.yearsEmployed,
      unionAffiliation: selectedRegistration.unionAffiliation,
      unionPosition: selectedRegistration.unionPosition,
      registeredDate: selectedRegistration.submittedDate ?? selectedRegistration.createdAt,
      emergencyContact: {
        fullName: selectedRegistration.emergencyContactName,
        relationship: selectedRegistration.emergencyContactRelationship,
        phone: selectedRegistration.emergencyContactPhone,
        address: selectedRegistration.emergencyContactAddress,
      },
      idPhotoPath: idPhotoEntry?.filePath ?? null,
      signaturePath: signatureEntry?.filePath ?? null,
    };
  };

  const handlePreviewForm = async () => {
    if (!selectedRegistration) return;
    setFormPreviewError("");
    setFormPreviewing(true);
    try {
      const mappedMember = buildPreviewMember();
      const previewUrl = await getMembershipFormPreviewUrl(mappedMember ?? selectedRegistration);
      if (previewUrl) {
        setFormPreviewUrl(previewUrl);
        setShowFormPreviewModal(true);
      }
    } catch (err) {
      setFormPreviewError("Unable to preview membership form. Please try again.");
    } finally {
      setFormPreviewing(false);
    }
  };

  const closeFormPreviewModal = () => {
    setShowFormPreviewModal(false);
    setFormPreviewUrl("");
  };

  const fileBaseUrl = API_BASE_URL;

  const resolveDocumentUrl = (path) => {
    return resolveBackendAssetUrl(path, { apiBaseUrl: fileBaseUrl });
  };

  const handleDocumentView = (doc) => {
    if (!doc?.filePath) return;
    const url = resolveDocumentUrl(doc.filePath);
    setPreviewDocument({ ...doc, action: "view", resolvedUrl: url });
  };

  const handleDocumentDownload = (doc) => {
    if (!doc?.filePath) return;
    const url = resolveDocumentUrl(doc.filePath);
    if (url) {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      const fallbackName = `${String(doc?.key ?? "document").replace(/\s+/g, "-")}.png`;
      anchor.download = fallbackName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }
    setPreviewDocument({ ...doc, action: "download" });
  };

  const handleCopyDocumentPath = async (path) => {
    if (!path) return;
    try {
      if (!navigator?.clipboard?.writeText) {
        setCopyStatus("Clipboard access not available.");
        return;
      }
      await navigator.clipboard.writeText(path);
      setCopyStatus("Copied to clipboard");
      setTimeout(() => setCopyStatus(""), 1500);
    } catch (clipboardError) {
      setCopyStatus("Unable to copy. Please copy manually.");
    }
  };

  const previewUrl = previewDocument?.resolvedUrl ?? (previewDocument ? resolveDocumentUrl(previewDocument.filePath) : null);

  const renderValidationRow = (label, valid) => (
    <div className="admin-validation-row" key={label}>
      <span>{label}</span>
      <span className={`admin-validation-status ${valid ? "is-valid" : "is-invalid"}`}>
        {valid ? <Check size={14} /> : <X size={14} />}
        {valid ? "Valid" : "Needs attention"}
      </span>
    </div>
  );

  return (
    <div className="admin-page admin-stack-xl registration-review-page">
      <header className="admin-surface rr-hero">
        <div className="rr-hero__intro">
          <span className="rr-hero__eyebrow">Registration Operations</span>
          <h1>Registration Review</h1>
          <p className="admin-muted">
            Review and approve pending member registrations ({overview.totalPending} waiting in queue).
          </p>
        </div>
        <div className="admin-actions rr-hero__actions">
          <button
            type="button"
            className={`admin-icon-button admin-icon-button--refresh ${loading ? "is-loading" : ""}`}
            onClick={() => setRefreshTick((value) => value + 1)}
            disabled={loading}
            aria-label="Refresh registration review"
            title="Refresh registration review"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      {error ? (
        <section className="admin-surface">
          <div className="admin-empty-state">{error}</div>
        </section>
      ) : null}

        <div className="admin-card-grid cols-4 rr-summary-grid">
        <button
          type="button"
          className={`admin-card admin-summary-card ${queueFilter === "all-pending" ? "is-active" : ""}`}
          onClick={() => setQueueFilter("all-pending")}
        >
          <div className="admin-card__label">Pending approvals</div>
          <div className="admin-row">
            <div className="admin-card__value">{overview.totalPending}</div>
            <span className="admin-chip is-orange"><Clock size={18} /></span>
          </div>
          <div className="admin-card__meta">AI pre-screen suggests 6 fast track</div>
        </button>
        <button
          type="button"
          className={`admin-card admin-summary-card ${queueFilter === "high-risk" ? "is-active" : ""}`}
          onClick={() => setQueueFilter("high-risk")}
        >
          <div className="admin-card__label">High risk cases</div>
          <div className="admin-row">
            <div className="admin-card__value">{overview.highPriority}</div>
            <span className="admin-chip is-red"><AlertTriangle size={18} /></span>
          </div>
          <div className="admin-card__meta">Need manual verification</div>
        </button>
        <button
          type="button"
          className={`admin-card admin-summary-card ${queueFilter === "high-risk" ? "is-active" : ""}`}
          onClick={() => setQueueFilter("high-risk")}
        >
          <div className="admin-card__label">Potential duplicates</div>
          <div className="admin-row">
            <div className="admin-card__value">{overview.duplicates}</div>
            <span className="admin-chip is-blue"><User size={18} /></span>
          </div>
          <div className="admin-card__meta">Auto-matched via email + employer</div>
        </button>
        <button
          type="button"
          className={`admin-card admin-summary-card ${queueFilter === "document-issue" ? "is-active" : ""}`}
          onClick={() => setQueueFilter("document-issue")}
        >
          <div className="admin-card__label">Incomplete documents</div>
          <div className="admin-row">
            <div className="admin-card__value">{overview.docIssues}</div>
            <span className="admin-chip is-orange"><FileText size={18} /></span>
          </div>
          <div className="admin-card__meta">AI flagged missing uploads</div>
        </button>
        </div>

        <div className="admin-registration-layout">
        <aside className="admin-queue-ap">
          <div className="admin-queue__header">
            <h2>Review Queue</h2>
            <label className="admin-queue-filter">
              <span>Filter</span>
              <select
                value={queueFilter}
                onChange={(event) => setQueueFilter(event.target.value)}
              >
                {queueFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="admin-queue__list-ap">
            {filteredQueue.map((registration) => {
              const isActive = registration.id === selectedId;
              const priorityTone =
                registration.priority === "High"
                  ? "is-red"
                  : registration.priority === "Normal"
                  ? "is-blue"
                  : "is-purple";
              const documentsComplete = registration.validationChecks?.documentsComplete;
              return (
                <button
                  type="button"
                  key={registration.id}
                  className={`admin-queue__item-ap ${isActive ? "is-active" : ""}`}
                  onClick={() => {
                    setSelectedId(registration.id);
                    setActiveTab("details");
                  }}
                >
                  <div className="admin-queue__item-header">
                    <div className="admin-queue__identity">
                      {renderRegistrationAvatar(registration, "admin-avatar--sm")}
                      <div>
                        <p className="admin-queue__name">{registration.fullName}</p>
                        <p className="admin-muted">{registration.company || "Company not set"}</p>
                      </div>
                    </div>
                    <span className={`admin-badge ${priorityTone}`}>
                      {registration.priority}
                    </span>
                  </div>
                  <div className="admin-queue__meta">
                    <span>{formatDate(registration.submittedDate)}</span>
                    <span>{registration.status}</span>
                  </div>
                  <div className="admin-queue__flags">
                    {registration.duplicateFlag ? (
                      <span className="admin-chip is-orange">
                        <AlertTriangle size={14} /> Duplicate warning
                      </span>
                    ) : null}
                    {documentsComplete === false ? (
                      <span className="admin-chip is-red">
                        <FileText size={14} /> Document issue
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
            {!loading && !filteredQueue.length ? (
              <div className="admin-empty-state">No registrations waiting in the queue.</div>
            ) : null}
            {loading ? <div className="admin-empty-state">Loading queue...</div> : null}
          </div>
        </aside>

        <section className="admin-registration-detail">
          {selectedRegistration ? (
            <div className="admin-surface admin-stack-lg">
              <header className="admin-registration-detail__header">
                <div className="admin-registration-detail__identity">
                  {renderRegistrationAvatar(selectedRegistration, "admin-avatar--lg")}
                  <div>
                    <h2>{selectedRegistration.fullName}</h2>
                    <p className="admin-muted">{selectedRegistration.email ?? "Email not provided"}</p>
                    <div className="admin-inline-list">
                      <span>
                        <Phone size={14} /> {selectedRegistration.phone ?? "Not provided"}
                      </span>
                      <span>
                        <Building2 size={14} /> {selectedRegistration.company ?? "Company not set"}
                      </span>
                      <span>
                        <MapPin size={14} /> {selectedRegistration.address ?? "Address unavailable"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="admin-registration-detail__actions">
                  <button
                    type="button"
                    className="admin-button"
                    onClick={() => {
                      setActionErrors((prev) => ({ ...prev, reject: "" }));
                      setShowRejectDialog(true);
                    }}
                  >
                    <X size={16} /> Reject
                  </button>
                  <button
                    type="button"
                    className="admin-button is-primary"
                    onClick={() => {
                      setActionErrors((prev) => ({ ...prev, approve: "" }));
                      setShowApproveDialog(true);
                    }}
                  >
                    <Check size={16} /> Approve registration
                  </button>
                </div>
              </header>

              <div className="admin-tabs">
                <div className="admin-tabs__list">
                  <button
                    type="button"
                    className={`admin-tabs__trigger ${activeTab === "details" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("details")}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    className={`admin-tabs__trigger ${activeTab === "validation" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("validation")}
                  >
                    Validation
                  </button>
                  <button
                    type="button"
                    className={`admin-tabs__trigger ${activeTab === "documents" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("documents")}
                  >
                    Documents
                  </button>
                </div>

                <div className="admin-tabs__content">
                  {activeTab === "details" ? (
                    <div className="admin-detail-grid">
                      <article className="admin-card">
                        <div className="admin-card__label admin-card__label--space-between">
                          <span>
                            <User size={16} /> Company profile
                          </span>
                        </div>
                        <div className="admin-stat">
                          <span>Company name</span>
                          <span>{selectedRegistration.company ?? "Not provided"}</span>
                        </div>
                        <div className="admin-stat">
                          <span>Union affiliation</span>
                          <span>{selectedRegistration.unionAffiliation ?? "Not provided"}</span>
                        </div>
                        <div className="admin-stat">
                          <span>Position in union</span>
                          <span>{selectedRegistration.unionPosition ?? "Not provided"}</span>
                        </div>
                        <div className="admin-stat">
                          <span>Union name</span>
                          <span>{selectedRegistration.department ?? "Not provided"}</span>
                        </div>
                      </article>

                      <article className="admin-card">
                        <div className="admin-card__label">
                          <Shield size={16} /> Compliance summary
                        </div>
                        <div className="admin-stat">
                          <span>Years employed</span>
                          <span>
                            {selectedRegistration.yearsEmployed != null
                              ? `${Number(selectedRegistration.yearsEmployed).toString()} years`
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="admin-stat">
                          <span>Submitted</span>
                          <span>{formatDate(selectedRegistration.submittedDate)}</span>
                        </div>
                        <div className="admin-stat">
                          <span>Status</span>
                          <span>{selectedRegistration.status ?? "Pending Review"}</span>
                        </div>
                      </article>

                      <article className="admin-card admin-stack-md">
                        <div className="admin-card__label">
                          <Clock size={16} /> Activity timeline
                        </div>
                        <div className="admin-timeline">
                          {(selectedRegistration?.timeline ?? []).map((item, index) => (
                            <div
                              key={`${selectedRegistration?.id ?? index}-${item.time ?? index}`}
                              className="admin-timeline__item"
                            >
                              <span>{item.time}</span>
                              <p>{item.detail}</p>
                            </div>
                          ))}
                        </div>
                      </article>

                      <article className="admin-card admin-stack-md">
                        <div className="admin-card__label">
                          <AlertTriangle size={16} /> Risk notes
                        </div>
                        <ul className="admin-list">
                          {(selectedRegistration?.riskNotes ?? []).map((note) => (
                            <li key={note}>{note}</li>
                          ))}
                        </ul>
                        {selectedRegistration.duplicateFlag ? (
                          <div className="admin-callout is-warning">
                            <div>
                              <strong>Potential duplicate detected</strong>
                              <p>
                                Similar identity found in database. Review the comparison list to verify conflict before approving.
                              </p>
                              <button
                                type="button"
                                className="admin-button admin-button--ghost"
                                onClick={openSimilarModal}
                              >
                                View similar member <ChevronRight size={14} />
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </article>

                    </div>
                  ) : null}

                  {activeTab === "validation" ? (
                    <div className="admin-grid-two">
                      <article className="admin-card admin-stack-md">
                        <div className="admin-card__label">
                          <CheckCircle2 size={16} /> Validation checks
                        </div>
                        {renderValidationRow("Photo ID uploaded", hasPhotoId)}
                        {renderValidationRow(
                          "Email address valid",
                          selectedRegistration?.validationChecks?.emailValid ?? false,
                        )}
                        {renderValidationRow(
                          "Phone number valid",
                          selectedRegistration?.validationChecks?.phoneValid ?? false,
                        )}
                        {renderValidationRow(
                          "Company verified",
                          selectedRegistration?.validationChecks?.companyVerified ?? false,
                        )}
                        {renderValidationRow(
                          "No duplicate conflicts",
                          selectedRegistration?.validationChecks?.noDuplicates ?? false,
                        )}
                      </article>

                      <article className="admin-card admin-stack-md">
                        <div className="admin-card__label">
                          <Mail size={16} /> Follow-up actions
                        </div>
                        <p className="admin-muted">
                          Document each contact attempt when requesting missing requirements.
                        </p>
                        <div className="admin-inline-list">
                          <span>
                            <Mail size={14} /> Send email reminder
                          </span>
                          <span>
                            <Phone size={14} /> Log phone follow-up
                          </span>
                          <span>
                            <Calendar size={14} /> Schedule onsite submission
                          </span>
                        </div>
                        <button type="button" className="admin-button admin-button--ghost">
                          Open contact log
                        </button>
                      </article>
                    </div>
                  ) : null}

                  {activeTab === "documents" ? (
                    <div className="admin-documents-grid">
                      <div className="admin-row">
                        <div className="admin-card__label">Member form preview</div>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost"
                          onClick={handlePreviewForm}
                          disabled={!selectedRegistration || formPreviewing}
                        >
                          {formPreviewing ? "Generating..." : "Preview membership form"}
                        </button>
                      </div>
                      {formPreviewError ? (
                        <div className="admin-callout is-danger">{formPreviewError}</div>
                      ) : null}
                      {documentFiles.length ? (
                        documentFiles.map((doc) => {
                          const isAvailable = Boolean(doc.filePath);
                          return (
                            <article key={doc.key} className="admin-card admin-stack-sm">
                              <div className="admin-card__label">
                                <FileText size={16} /> {doc.label}
                              </div>
                              <p className="admin-muted">{doc.status}</p>
                              <div className="admin-row">
                                <button
                                  type="button"
                                  className="admin-button admin-button--ghost"
                                  onClick={() => handleDocumentView(doc)}
                                  disabled={!isAvailable}
                                >
                                  <Eye size={14} /> View
                                </button>
                                <button
                                  type="button"
                                  className="admin-button admin-button--ghost"
                                  onClick={() => handleDocumentDownload(doc)}
                                  disabled={!isAvailable}
                                >
                                  <Download size={14} /> Download
                                </button>
                              </div>
                            </article>
                          );
                        })
                      ) : (
                        <div className="admin-empty-state">No document metadata available.</div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-placeholder">
              <User size={32} />
              <h3>Select a registration</h3>
              <p className="admin-muted">
                Choose a registration from the queue to review details and take action.
              </p>
            </div>
          )}
        </section>
        </div>

      <section className="admin-surface admin-stack-md rr-section rr-section--history">
        <div className="admin-row">
          <h2>Approval history</h2>
          <span className="admin-pill">Latest actions</span>
        </div>
        {historyError ? <div className="admin-callout is-danger">{historyError}</div> : null}
        <div className="admin-table-wrapper admin-scroll">
          <table className="admin-table admin-table--resizable" ref={historyTableRef}>
            <colgroup>
              {["Name", "Time", "Admin", "Reason", "Date"].map((label, index) => (
                <col
                  key={label}
                  style={
                    historyColumnWidths[index]
                      ? { width: `${historyColumnWidths[index]}px` }
                      : undefined
                  }
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {[
                  { key: "name", label: "Name" },
                  { key: "time", label: "Time" },
                  { key: "admin", label: "Admin" },
                  { key: "reason", label: "Reason" },
                  { key: "date", label: "Date" },
                ].map((column, index) => (
                  <th key={column.key}>
                    {column.label}
                    <span
                      className="admin-table__resizer"
                      role="presentation"
                      onMouseDown={(event) => startHistoryResize(index, event)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedHistory.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.memberName ?? "--"}</td>
                  <td>{formatTime(entry.createdAt)}</td>
                  <td>{entry.adminName ?? "--"}</td>
                  <td>
                    {entry.action === "Rejected"
                      ? (() => {
                        const reason = String(entry.reason ?? "").trim();
                        const category = String(entry.category ?? "").trim();
                        if (!reason) return "No reason provided";
                        if (!category) return reason;

                        const reasonLower = reason.toLowerCase();
                        const categoryLower = category.toLowerCase();
                        const reasonAlreadyIncludesCategory =
                          reasonLower === categoryLower
                          || reasonLower.startsWith(`${categoryLower} -`)
                          || reasonLower.startsWith(`${categoryLower}:`);

                        return reasonAlreadyIncludesCategory
                          ? reason
                          : `${reason} • ${category}`;
                      })()
                      : "Approved"}
                  </td>
                  <td>{formatDate(entry.createdAt)}</td>
                </tr>
              ))}
              {!historyLoading && history.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="admin-empty-state">No approval history yet.</div>
                  </td>
                </tr>
              ) : null}
              {historyLoading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="admin-empty-state">Loading history...</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="admin-row rr-pagination" role="navigation" aria-label="Registration history pagination">
          <span className="admin-muted">
            Page {historyPageClamped + 1} of {historyTotalPages}
          </span>
          <span className="admin-pill is-blue">
            Showing {pagedHistory.length} of {history.length}
          </span>
          <div className="admin-actions rr-pagination__controls">
            <button
              type="button"
              className="admin-button admin-button--ghost rr-pager-button"
              onClick={() => setHistoryPage((prev) => Math.max(0, prev - 1))}
              disabled={historyPageClamped === 0}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <button
              type="button"
              className="admin-button admin-button--ghost rr-pager-button"
              onClick={() => setHistoryPage((prev) => Math.min(historyTotalPages - 1, prev + 1))}
              disabled={historyPageClamped >= historyTotalPages - 1}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <section className="admin-surface admin-stack-md rr-section rr-section--guided">
        <h2>Guided actions</h2>
        <div className="admin-card-grid cols-3">
          <article className="admin-card">
            <div className="admin-card__label">Fast-track approvals</div>
            <p className="admin-muted">
              {guidedActions.fastTrackCount > 0
                ? `${guidedActions.pluralize(guidedActions.fastTrackCount, "low-risk application")} ready for bulk approval.`
                : "No low-risk applications are ready for fast-track approval."}
            </p>
          </article>
          <article className="admin-card">
            <div className="admin-card__label">Manual review alerts</div>
            <p className="admin-muted">
              {guidedActions.manualReviewCount > 0
                ? `${guidedActions.pluralize(guidedActions.manualReviewCount, "application")} need manual verification.`
                : "No manual review alerts detected."}
            </p>
          </article>
          <article className="admin-card">
            <div className="admin-card__label">Weekly summary</div>
            <p className="admin-muted">
              {`${guidedActions.pluralize(guidedActions.approvalsWeek, "approved")} • ${guidedActions.pluralize(guidedActions.rejectionsWeek, "rejected")} • ${guidedActions.pluralize(guidedActions.awaitingDocs, "awaiting document")}`}
            </p>
          </article>
        </div>
      </section>

      {showApproveDialog ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeApproveDialog} />
            <div className="admin-dialog__panel rr-action-dialog">
            <header className="admin-dialog__header">
              <h3>Approve registration</h3>
            </header>
            <div className="admin-dialog__body admin-stack-md">
                <p className="rr-dialog-lead">
                  Confirm approval for <span className="rr-dialog-name">{selectedRegistration?.fullName}</span>
                </p>
                <ul className="admin-approve-list rr-approve-checklist" aria-label="Approval actions">
                {[
                  "Generate a digital ID for the member",
                  "Send approval notification via email",
                  "Activate the member account",
                  "Add member to the active directory",
                ].map((item) => (
                    <li key={item} className="admin-approve-list__item rr-approve-checklist__item">
                      <span aria-hidden className="rr-approve-checklist__icon">
                        <Check size={13} />
                      </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {actionErrors.approve ? (
                <div className="admin-callout is-danger">{actionErrors.approve}</div>
              ) : null}
            </div>
            <footer className="admin-dialog__footer">
              <button type="button" className="admin-button admin-button--ghost" onClick={closeApproveDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-primary"
                onClick={handleApprove}
                disabled={isApproveProcessing}
              >
                {isApproveProcessing ? "Processing..." : "Approve registration"}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {showRejectDialog ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={closeRejectDialog} />
            <div className="admin-dialog__panel rr-action-dialog">
            <header className="admin-dialog__header">
              <h3>Reject registration</h3>
            </header>
            <div className="admin-dialog__body admin-stack-md">
                <label className="admin-field rr-dialog-field">
                <span>Rejection reason</span>
                <select value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)}>
                  {rejectionReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </label>
                <label className="admin-field rr-dialog-field">
                  <span>Additional remarks (optional)</span>
                  <textarea
                    rows={4}
                    value={customReason}
                    onChange={(event) => setCustomReason(event.target.value)}
                    placeholder="Add specific feedback for this applicant"
                  />
                </label>
              {actionErrors.reject ? (
                <div className="admin-callout is-danger">{actionErrors.reject}</div>
              ) : null}
            </div>
            <footer className="admin-dialog__footer">
                <button type="button" className="admin-button admin-button--ghost" onClick={closeRejectDialog}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-danger"
                onClick={handleReject}
                disabled={isRejectProcessing}
              >
                {isRejectProcessing ? "Processing..." : "Reject registration"}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {showFormPreviewModal ? (
        <div className="admin-dialog" role="dialog" aria-modal="true" aria-labelledby="membership-form-preview-title">
          <div className="admin-dialog__backdrop" onClick={closeFormPreviewModal} />
          <div className="admin-dialog__panel" role="document">
            <header className="admin-dialog__header">
              <h3 id="membership-form-preview-title">Membership form preview</h3>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={closeFormPreviewModal}
                aria-label="Close membership form preview"
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body">
              {formPreviewUrl ? (
                <iframe
                  title="Membership Form Preview"
                  src={formPreviewUrl}
                  style={{ width: "100%", minHeight: "75vh", border: "1px solid var(--admin-card-border)", borderRadius: "12px", background: "var(--admin-card-bg)" }}
                />
              ) : (
                <div className="admin-empty-state">Preview not available.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showSimilarModal ? (
        <div className="admin-dialog" role="dialog" aria-modal="true" aria-labelledby="similar-member-title">
          <div className="admin-dialog__backdrop" onClick={closeSimilarModal} />
          <div className="admin-dialog__panel admin-stack-md rr-similar-dialog" role="document">
            <header className="admin-dialog__header">
              <div>
                <h3 id="similar-member-title">Similar member comparison</h3>
                <p className="admin-muted">Review exact overlaps before approving or rejecting this registration.</p>
              </div>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              {similarLoading ? <div className="admin-empty-state">Loading similar members...</div> : null}
              {similarError ? <div className="admin-callout is-danger">{similarError}</div> : null}

              {!similarLoading && !similarError && similarComparison.matches.length === 0 ? (
                <div className="admin-empty-state">No likely duplicate members found.</div>
              ) : null}

              {!similarLoading && !similarError && similarComparison.matches.length > 0 ? (
                <div className="admin-stack-sm">
                  {similarComparison.matches.map((entry, index) => {
                    const nameMatchClass = entry?.overlap?.nameExact ? "admin-compare-value is-match" : "admin-compare-value";
                    const phoneMatchClass = entry?.overlap?.phoneExact ? "admin-compare-value is-match" : "admin-compare-value";
                    const emailMatchClass = entry?.overlap?.emailExact ? "admin-compare-value is-match" : "admin-compare-value";
                    const companyMatchClass = "admin-compare-value";
                    const dobMatchClass = "admin-compare-value";
                    const isAdminAccount = entry?.accountType === "admin";
                    const sourceName = String(similarComparison?.source?.fullName ?? selectedRegistration?.fullName ?? "Pending applicant").trim();
                    const matchName = String(entry?.fullName ?? `User #${entry?.id ?? "--"}`).trim();

                    return (
                      <article key={`${entry.accountType ?? "member"}-${entry.id}`} className="admin-card admin-stack-sm">
                        <div className="admin-card__label admin-card__label--space-between">
                          <span>{`Match #${index + 1}`}</span>
                        </div>
                        <div className="admin-compare-grid">
                          <span aria-hidden />
                          <span className="admin-compare-column-title">Pending Application</span>
                          <span className="admin-compare-column-title admin-compare-column-title--right">
                            <span>Existing Member</span>
                            {isAdminAccount ? <span className="admin-chip is-red">Admin Account</span> : null}
                          </span>

                          <>
                            <span className="admin-muted">Full name</span>
                            <span className={nameMatchClass}>{sourceName || "Not provided"}</span>
                            <span className={nameMatchClass}>{matchName || "Not available"}</span>
                          </>

                          <span className="admin-muted">Phone</span>
                          <span className={phoneMatchClass}>{similarComparison?.source?.phone ?? "Not provided"}</span>
                          <span className={phoneMatchClass}>{entry.phone ?? "Not available"}</span>

                          <span className="admin-muted">Email</span>
                          <span className={emailMatchClass}>{similarComparison?.source?.email ?? "Not provided"}</span>
                          <span className={emailMatchClass}>{entry.email ?? "Not provided"}</span>

                          <span className="admin-muted">Company</span>
                          <span className={companyMatchClass}>{similarComparison?.source?.company ?? "Not provided"}</span>
                          <span className={companyMatchClass}>{entry.company ?? "Not available"}</span>

                          <span className="admin-muted">Date of birth</span>
                          <span className={dobMatchClass}>{similarComparison?.source?.dateOfBirth ? formatDate(similarComparison.source.dateOfBirth) : "Not provided"}</span>
                          <span className={dobMatchClass}>{entry?.dateOfBirth ? formatDate(entry.dateOfBirth) : "Not available"}</span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <footer className="admin-dialog__footer">
              <button type="button" className="admin-button" onClick={closeSimilarModal}>
                Close
              </button>
              <button
                type="button"
                className="admin-button is-danger"
                onClick={() => {
                  closeSimilarModal();
                  setActionErrors((prev) => ({ ...prev, reject: "" }));
                  setShowRejectDialog(true);
                }}
              >
                Reject registration
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {previewDocument ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" onClick={() => setPreviewDocument(null)} />
          <div className="admin-dialog__panel">
            <header className="admin-dialog__header">
              <h3>{previewDocument.label}</h3>
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={() => setPreviewDocument(null)}
                aria-label="Close document preview"
              >
                <X size={16} />
              </button>
            </header>
            <div className="admin-dialog__body admin-stack-md">
              {previewUrl ? (
                <div className="admin-document-preview">
                  <img src={previewUrl} alt={previewDocument.label ?? "Document preview"} />
                </div>
              ) : (
                <div className="admin-callout is-danger">Document preview unavailable. Stored path shown below.</div>
              )}
              <div>
                <strong>Stored path</strong>
                <p className="admin-muted">{previewDocument.filePath ?? "Not available"}</p>
              </div>
              <div>
                <strong>Verification status</strong>
                <p className="admin-muted">
                  {previewDocument.verifiedAt
                    ? `Verified ${formatDateTime(previewDocument.verifiedAt)}`
                    : "Pending admin verification"}
                </p>
              </div>
              <div>
                <strong>Source</strong>
                <p className="admin-muted">
                  {previewDocument.source === "user_documents"
                    ? "Uploaded via user documents"
                    : previewDocument.source === "registration_forms"
                    ? "Provided in registration form"
                    : "Unknown source"}
                </p>
              </div>
              {copyStatus ? <div className="admin-callout is-info">{copyStatus}</div> : null}
            </div>
            <footer className="admin-dialog__footer">
              <button
                type="button"
                className="admin-button admin-button--ghost"
                onClick={() => handleCopyDocumentPath(previewDocument.filePath)}
                disabled={!previewDocument.filePath}
              >
                <Copy size={14} /> Copy path
              </button>
              <button
                type="button"
                className="admin-button"
                onClick={() => setPreviewDocument(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
